import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  DataTable,
  FormLayout,
  InlineStack,
  List,
  Modal,
  Pagination,
  Select,
  Spinner,
  Text,
  TextField
} from '@shopify/polaris';
import { accountingOperationsService } from '../services/accountingOperationsService';
import { authService } from '../services/authService';
import { accountingHelpers } from '../constants/accountingConstants';

const today = () => new Date().toISOString().slice(0, 10);
const IMPORT_PREVIEW_PAGE_SIZE = 100;

const emptyForm = () => ({
  provider: 'paymob',
  reference: '',
  payout_date: today(),
  currency: authService.getActiveCountry() === 'UAE' ? 'AED' : 'EGP',
  gross_amount: '',
  fee_amount: '0',
  net_amount: '',
  notes: ''
});

const getCustomerName = order => (
  order.customer?.name
  || [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ')
  || 'Unknown'
);

const subtractAmounts = (amount, deduction, clampAtZero = false) => {
  const difference = Number(amount || 0) - Number(deduction || 0);
  return (clampAtZero ? Math.max(0, difference) : difference).toFixed(2);
};

const PayoutsPage = () => {
  const [payouts, setPayouts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [eligiblePagination, setEligiblePagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [eligibleSearch, setEligibleSearch] = useState('');
  const [selectedOrdersById, setSelectedOrdersById] = useState({});
  const [manualGrossAmount, setManualGrossAmount] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewPage, setImportPreviewPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submissionKey, setSubmissionKey] = useState('');
  const [payoutToCollect, setPayoutToCollect] = useState(null);
  const importRequestRef = useRef(0);

  const loadPayouts = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const response = await accountingOperationsService.getPayouts({
        page,
        limit: 20,
        status,
        provider,
        search
      });
      setPayouts(response.data || []);
      setPagination(response.pagination || { page, totalPages: 1, total: 0 });
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [provider, search, status]);

  useEffect(() => {
    loadPayouts(1);
  }, [loadPayouts]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadEligibleOrders = useCallback(async (selectedProvider, query = '', page = 1) => {
    setOrdersLoading(true);
    setError('');
    try {
      const response = await accountingOperationsService.getEligibleOrders({
        provider: selectedProvider,
        search: query,
        page,
        limit: 20
      });
      const nextOrders = response.data || [];
      setEligibleOrders(nextOrders);
      setEligiblePagination(response.pagination || { page, totalPages: 1, total: 0 });
      setForm(current => ({
        ...current,
        currency: nextOrders[0]?.currency || current.currency,
      }));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const openCreateModal = useCallback(async () => {
    const nextForm = emptyForm();
    setForm(nextForm);
    setSelectedOrdersById({});
    setManualGrossAmount('');
    setEligibleSearch('');
    setImportFile(null);
    setImportPreview(null);
    setImportPreviewPage(1);
    setImportLoading(false);
    importRequestRef.current += 1;
    setSubmissionKey(`payout-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setShowCreate(true);
    await loadEligibleOrders(nextForm.provider);
  }, [loadEligibleOrders]);

  useEffect(() => {
    window.addEventListener('accountingPayoutsCreate', openCreateModal);
    return () => window.removeEventListener('accountingPayoutsCreate', openCreateModal);
  }, [openCreateModal]);

  const updateAmounts = (selectedById, feeValue = form.fee_amount) => {
    const selected = Object.values(selectedById);
    setForm(current => {
      const gross = selected.length > 0
        ? selected.reduce((sum, order) => sum + Number(order.amount || 0), 0)
        : Number(current.gross_amount || 0);
      const fee = Number(feeValue || 0);
      return {
        ...current,
        gross_amount: selected.length > 0 ? gross.toFixed(2) : current.gross_amount,
        fee_amount: feeValue,
        net_amount: current.gross_amount || selected.length > 0
          ? subtractAmounts(gross, fee, true)
          : ''
      };
    });
  };

  const toggleOrder = (orderId) => {
    const next = { ...selectedOrdersById };
    if (next[orderId]) {
      delete next[orderId];
    } else {
      next[orderId] = eligibleOrders.find(order => order.order_id === orderId);
    }
    setSelectedOrdersById(next);
    if (Object.keys(next).length > 0) {
      updateAmounts(next);
      return;
    }
    const gross = Number(manualGrossAmount || 0);
    const fee = Number(form.fee_amount || 0);
    setForm(current => ({
      ...current,
      gross_amount: manualGrossAmount,
      net_amount: manualGrossAmount ? subtractAmounts(gross, fee, true) : ''
    }));
  };

  const handleFeeChange = (value) => {
    if (importPreview?.ready) {
      setForm(current => ({
        ...current,
        fee_amount: value,
        net_amount: value === ''
          ? ''
          : subtractAmounts(current.gross_amount, value)
      }));
      return;
    }
    updateAmounts(selectedOrdersById, value);
  };

  const handleNetChange = (value) => {
    setForm(current => ({
      ...current,
      net_amount: value,
      fee_amount: value === ''
        ? ''
        : subtractAmounts(current.gross_amount, value)
    }));
  };

  const handleGrossChange = (value) => {
    const gross = Number(value || 0);
    const fee = Number(form.fee_amount || 0);
    setManualGrossAmount(value);
    setForm(current => ({
      ...current,
      gross_amount: value,
      net_amount: value === '' ? '' : subtractAmounts(gross, fee, true)
    }));
  };

  const clearImportPreview = () => {
    importRequestRef.current += 1;
    setImportLoading(false);
    setImportFile(null);
    setImportPreview(null);
    setImportPreviewPage(1);
    setSelectedOrdersById({});
    setManualGrossAmount('');
    setForm(current => ({
      ...current,
      gross_amount: '',
      fee_amount: '0',
      net_amount: ''
    }));
  };

  const handlePayoutFile = async (file) => {
    if (!file) return;
    clearImportPreview();
    const requestId = ++importRequestRef.current;
    const importProvider = form.provider;
    setImportLoading(true);
    setImportFile(file);
    setError('');
    setSuccess('');
    try {
      const response = importProvider === 'bosta'
        ? await accountingOperationsService.previewBostaPayoutXlsx(file)
        : await accountingOperationsService.previewPaymobPayoutCsv(file);
      if (requestId !== importRequestRef.current) return;
      const preview = response.data;
      setImportPreview(preview);
      setImportPreviewPage(1);
      const selected = Object.fromEntries(
        (preview.orders || []).map(order => [order.order_id, order])
      );
      setSelectedOrdersById(selected);
      setManualGrossAmount('');
      setForm(current => ({
        ...current,
        provider: preview.provider || importProvider,
        reference: preview.reference || current.reference,
        payout_date: preview.payout_date || current.payout_date,
        currency: preview.currency,
        gross_amount: Number(preview.gross_amount).toFixed(2),
        fee_amount: Number(preview.fee_amount).toFixed(2),
        net_amount: Number(preview.net_amount).toFixed(2)
      }));
      if (!preview.ready) {
        setError('This payout file contains rows that cannot be safely imported. Review the details below.');
        return;
      }

      const unlinkedCount = preview.counts.unmatched_transactions
        + preview.counts.ineligible_orders;
      setSuccess(
        `${preview.counts.included_transactions} transaction`
        + `${preview.counts.included_transactions === 1 ? '' : 's'} included; `
        + `${preview.counts.matched_orders} order`
        + `${preview.counts.matched_orders === 1 ? '' : 's'} linked`
        + `${unlinkedCount > 0 ? `. ${unlinkedCount} unlinked row${unlinkedCount === 1 ? '' : 's'} will remain in the payout totals` : ''}.`
      );
    } catch (uploadError) {
      if (requestId !== importRequestRef.current) return;
      setImportFile(null);
      setImportPreview(null);
      setSelectedOrdersById({});
      setError(uploadError.message);
    } finally {
      if (requestId === importRequestRef.current) setImportLoading(false);
    }
  };

  const closeCreateModal = () => {
    if (submitting) return;
    importRequestRef.current += 1;
    setImportLoading(false);
    setShowCreate(false);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setError('');
    try {
      if (importPreview?.ready && importFile) {
        if (form.provider === 'bosta') {
          await accountingOperationsService.createBostaPayoutXlsx(
            importFile,
            {
              notes: form.notes,
              fee_amount: form.fee_amount,
              net_amount: form.net_amount
            },
            submissionKey
          );
        } else {
          await accountingOperationsService.createPaymobPayoutCsv(
            importFile,
            {
              reference: form.reference,
              payout_date: form.payout_date,
              notes: form.notes,
              fee_amount: form.fee_amount,
              net_amount: form.net_amount
            },
            submissionKey
          );
        }
      } else {
        await accountingOperationsService.createPayout({
          ...form,
          gross_amount: Number(form.gross_amount),
          fee_amount: Number(form.fee_amount),
          net_amount: Number(form.net_amount),
          order_ids: Object.keys(selectedOrdersById)
        }, submissionKey);
      }
      setShowCreate(false);
      setSuccess('Payout recorded as pending.');
      await loadPayouts(1);
    } catch (createError) {
      setError(createError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCollect = async () => {
    if (!payoutToCollect) return;
    setSubmitting(true);
    setError('');
    try {
      await accountingOperationsService.collectPayout(payoutToCollect.payout_id);
      setPayoutToCollect(null);
      setSuccess('Payout marked as collected.');
      await loadPayouts(pagination.page);
    } catch (collectError) {
      setError(collectError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const rows = payouts.map(payout => [
    payout.payout_date ? new Date(payout.payout_date).toLocaleDateString() : '-',
    payout.reference || payout.payout_id,
    payout.provider === 'bosta' ? 'Bosta' : 'Paymob',
    payout.order_count || 0,
    accountingHelpers.formatCurrency(payout.gross_amount, payout.currency),
    accountingHelpers.formatCurrency(payout.fee_amount, payout.currency),
    accountingHelpers.formatCurrency(payout.net_amount, payout.currency),
    payout.status === 'collected'
      ? <Badge tone="success">Collected</Badge>
      : <Badge tone="attention">Pending</Badge>,
    payout.status === 'pending'
      ? <Button size="slim" onClick={() => setPayoutToCollect(payout)}>Mark collected</Button>
      : null
  ]);

  const eligibleRows = eligibleOrders.map(order => [
    <Checkbox
      label={`Select ${order.order_id}`}
      labelHidden
      checked={Boolean(selectedOrdersById[order.order_id])}
      onChange={() => toggleOrder(order.order_id)}
    />,
    order.order_id,
    getCustomerName(order),
    order.payment_reference || order.tracking_number || '-',
    accountingHelpers.formatCurrency(order.amount, order.currency)
  ]);
  const importedOrders = importPreview?.orders || [];
  const importPreviewPages = Math.max(
    1,
    Math.ceil(importedOrders.length / IMPORT_PREVIEW_PAGE_SIZE)
  );
  const importMatchedRows = importedOrders
    .slice(
      (importPreviewPage - 1) * IMPORT_PREVIEW_PAGE_SIZE,
      importPreviewPage * IMPORT_PREVIEW_PAGE_SIZE
    )
    .map(order => [
      order.order_id,
      getCustomerName(order),
      order.source_reference || order.paymob_transaction_id || '-',
      accountingHelpers.formatCurrency(order.amount, order.currency),
      accountingHelpers.formatCurrency(order.source_fee ?? order.paymob_fee, order.currency)
    ]);
  const unmatchedImportIssues = importPreview?.unmatched || [];
  const ineligibleImportIssues = importPreview?.ineligible || [];
  const visibleUnmatchedIssues = unmatchedImportIssues.slice(0, 20);
  const visibleIneligibleIssues = ineligibleImportIssues.slice(
    0,
    Math.max(0, 20 - visibleUnmatchedIssues.length)
  );
  const visibleImportIssues = [
    ...visibleUnmatchedIssues.map(item => (
      `${item.transaction_id || item.tracking_number || item.merchant_order_id || 'Unknown row'}: `
      + `${item.reason || 'No matching order found'}`
    )),
    ...visibleIneligibleIssues.map(item => (
      `${item.order_id || item.tracking_number || item.merchant_order_id || 'Unknown row'}: `
      + item.reason
    ))
  ];
  const hiddenImportIssueCount = Math.max(
    0,
    unmatchedImportIssues.length + ineligibleImportIssues.length - visibleImportIssues.length
  );
  const hasImportIssues = importPreview && (
    importPreview.counts.unmatched_transactions > 0
    || importPreview.counts.ineligible_orders > 0
    || importPreview.counts.unmatchable_transactions > 0
  );

  const selectedOrderCount = Object.keys(selectedOrdersById).length;
  const bostaImportReady = form.provider === 'bosta' && importPreview?.ready;
  const grossAmount = Number(form.gross_amount);
  const feeAmount = Number(form.fee_amount);
  const netAmount = Number(form.net_amount);
  const canCreate = !importLoading
    && (!importPreview || importPreview.ready)
    && form.reference.trim()
    && grossAmount > 0
    && feeAmount >= 0
    && feeAmount <= grossAmount
    && netAmount >= 0
    && Math.abs(grossAmount - feeAmount - netAmount) <= 0.01;

  return (
    <>
      <BlockStack gap="400">
        <Text as="p" tone="subdued">
          {pagination.total || 0} payout{pagination.total === 1 ? '' : 's'}
        </Text>
        {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
        {success && <Banner tone="success" onDismiss={() => setSuccess('')}>{success}</Banner>}
        <Card>
          <BlockStack gap="300">
            <InlineStack gap="300" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search reference"
                  value={searchInput}
                  onChange={setSearchInput}
                  clearButton
                  onClearButtonClick={() => setSearchInput('')}
                  autoComplete="off"
                />
              </div>
              <Select
                label="Provider"
                value={provider}
                onChange={setProvider}
                options={[
                  { label: 'All providers', value: '' },
                  { label: 'Paymob', value: 'paymob' },
                  { label: 'Bosta', value: 'bosta' }
                ]}
              />
              <Select
                label="Status"
                value={status}
                onChange={setStatus}
                options={[
                  { label: 'All statuses', value: '' },
                  { label: 'Pending', value: 'pending' },
                  { label: 'Collected', value: 'collected' }
                ]}
              />
            </InlineStack>
            {loading ? (
              <InlineStack align="center"><Spinner accessibilityLabel="Loading payouts" /></InlineStack>
            ) : payouts.length === 0 ? (
              <Text as="p" tone="subdued">No payouts match the selected filters.</Text>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                headings={['Date', 'Reference', 'Provider', 'Orders', 'Gross', 'Fees', 'Net', 'Status', 'Action']}
                rows={rows}
              />
            )}
            <InlineStack align="center">
              <Pagination
                hasPrevious={pagination.page > 1}
                onPrevious={() => loadPayouts(pagination.page - 1)}
                hasNext={pagination.page < pagination.totalPages}
                onNext={() => loadPayouts(pagination.page + 1)}
                label={`Page ${pagination.page || 1} of ${Math.max(pagination.totalPages || 1, 1)}`}
              />
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>

      <Modal
        open={showCreate}
        onClose={closeCreateModal}
        title="Record payout"
        primaryAction={{
          content: 'Save pending payout',
          onAction: handleCreate,
          loading: submitting,
          disabled: !canCreate
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: closeCreateModal,
          disabled: submitting
        }]}
        large
      >
        <Modal.Section>
          <BlockStack gap="400">
            {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
            <Banner tone="info">
              Linking eligible orders is optional. When orders are selected, Gross is calculated from their total.
            </Banner>
            <Select
              label="Provider"
              value={form.provider}
              disabled={Boolean(importPreview?.ready)}
              onChange={(value) => {
                clearImportPreview();
                const gross = Number(manualGrossAmount || 0);
                const fee = Number(form.fee_amount || 0);
                setForm(current => ({
                  ...current,
                  provider: value,
                  gross_amount: manualGrossAmount,
                  net_amount: manualGrossAmount
                    ? subtractAmounts(gross, fee, true)
                    : ''
                }));
                setSelectedOrdersById({});
                loadEligibleOrders(value);
              }}
              options={[
                { label: 'Paymob', value: 'paymob' },
                { label: 'Bosta', value: 'bosta' }
              ]}
            />
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">
                {form.provider === 'bosta' ? 'Bosta payout XLSX' : 'Paymob transactions CSV'}
              </Text>
              <Text as="p" tone="subdued">
                {form.provider === 'bosta'
                  ? 'Upload a Bosta payout workbook to match Paid rows using Order Id and the order tracking number.'
                  : 'Upload a Paymob transactions CSV to match successful payments using Merchant Order ID.'}
              </Text>
              <InlineStack gap="200" blockAlign="center">
                <label htmlFor="payout-import-file">
                  {form.provider === 'bosta' ? 'Upload XLSX' : 'Upload CSV'}
                </label>
                <input
                  id="payout-import-file"
                  type="file"
                  accept={form.provider === 'bosta'
                    ? '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    : '.csv,text/csv'}
                  disabled={importLoading || submitting}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    handlePayoutFile(file);
                  }}
                />
                {importLoading && <Spinner size="small" accessibilityLabel="Matching payout file" />}
                {importFile && <Text as="span">{importFile.name}</Text>}
                {importPreview && (
                  <Button onClick={clearImportPreview} disabled={importLoading || submitting}>
                    Clear file
                  </Button>
                )}
              </InlineStack>
              {importPreview && (
                <Text as="p" tone={importPreview.ready ? 'success' : 'critical'}>
                  {importPreview.counts.matched_orders} matched,{' '}
                  {importPreview.counts.unmatched_transactions} unmatched,{' '}
                  {importPreview.counts.ineligible_orders} ineligible,{' '}
                  {importPreview.counts.unmatchable_transactions} unmatchable
                </Text>
              )}
              {hasImportIssues && (
                <Banner
                  tone={importPreview.ready ? 'warning' : 'critical'}
                  title={importPreview.ready
                    ? 'Some rows will not be linked to orders'
                    : 'Some rows prevent this payout from being imported'}
                >
                  {visibleImportIssues.length > 0 && (
                    <List>
                      {visibleImportIssues.map((item, index) => (
                        <List.Item key={`${item}-${index}`}>{item}</List.Item>
                      ))}
                    </List>
                  )}
                  {importPreview.counts.unmatchable_transactions > 0 && (
                    <Text as="p">
                      {importPreview.counts.unmatchable_transactions} duplicate or invalid row
                      {importPreview.counts.unmatchable_transactions === 1 ? '' : 's'} must be fixed.
                    </Text>
                  )}
                  {hiddenImportIssueCount > 0 && (
                    <Text as="p">{hiddenImportIssueCount} additional row issues are not shown.</Text>
                  )}
                </Banner>
              )}
            </BlockStack>
            <FormLayout>
              <InlineStack gap="300">
                <TextField
                  label="Payout reference"
                  value={form.reference}
                  onChange={(value) => setForm(current => ({ ...current, reference: value }))}
                  disabled={Boolean(bostaImportReady)}
                  autoComplete="off"
                />
                <TextField
                  label="Payout date"
                  type="date"
                  value={form.payout_date}
                  onChange={(value) => setForm(current => ({ ...current, payout_date: value }))}
                  disabled={Boolean(bostaImportReady)}
                  autoComplete="off"
                />
              </InlineStack>
              <InlineStack gap="300">
                <TextField
                  label="Gross"
                  type="number"
                  value={form.gross_amount}
                  onChange={handleGrossChange}
                  disabled={selectedOrderCount > 0 || Boolean(importPreview?.ready)}
                  autoComplete="off"
                  helpText="Enter the payout gross amount, or select orders to calculate it"
                />
                <TextField
                  label="Fees"
                  type="number"
                  min={0}
                  value={form.fee_amount}
                  onChange={handleFeeChange}
                  disabled={submitting}
                  autoComplete="off"
                  helpText={importPreview?.ready
                    ? 'Adjust for bank transfer fees or other payout deductions'
                    : undefined}
                />
                <TextField
                  label="Net"
                  type="number"
                  min={0}
                  value={form.net_amount}
                  onChange={handleNetChange}
                  disabled={!importPreview?.ready || submitting}
                  autoComplete="off"
                  helpText={importPreview?.ready
                    ? 'Changing Net recalculates Fees'
                    : undefined}
                />
              </InlineStack>
              <TextField
                label="Notes"
                value={form.notes}
                onChange={(value) => setForm(current => ({ ...current, notes: value }))}
                multiline={2}
                autoComplete="off"
              />
            </FormLayout>
            {importPreview ? (
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">
                  Matched orders ({selectedOrderCount})
                </Text>
                {importMatchedRows.length > 0 ? (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric']}
                    headings={[
                      'Order',
                      'Customer',
                      form.provider === 'bosta' ? 'Bosta order' : 'Paymob transaction',
                      'Amount',
                      'Fee'
                    ]}
                    rows={importMatchedRows}
                  />
                ) : (
                  <Text as="p" tone="subdued">
                    {importPreview.ready
                      ? 'No orders could be linked. The provider totals can still be recorded.'
                      : 'No orders could be linked. Resolve the blocking file issues above.'}
                  </Text>
                )}
                {importPreviewPages > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={importPreviewPage > 1}
                      onPrevious={() => setImportPreviewPage(page => page - 1)}
                      hasNext={importPreviewPage < importPreviewPages}
                      onNext={() => setImportPreviewPage(page => page + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            ) : (
              <>
                <Text as="h3" variant="headingMd">
                  Optional eligible orders ({selectedOrderCount} selected)
                </Text>
                <InlineStack gap="200" blockAlign="end">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="Find eligible orders"
                      value={eligibleSearch}
                      onChange={setEligibleSearch}
                      clearButton
                      onClearButtonClick={() => setEligibleSearch('')}
                      autoComplete="off"
                      placeholder="Order, customer, transaction, or tracking reference"
                    />
                  </div>
                  <Button
                    onClick={() => loadEligibleOrders(form.provider, eligibleSearch)}
                    loading={ordersLoading}
                  >
                    Search
                  </Button>
                </InlineStack>
                {ordersLoading ? (
                  <InlineStack align="center"><Spinner accessibilityLabel="Loading eligible orders" /></InlineStack>
                ) : eligibleOrders.length === 0 ? (
                  <Text as="p" tone="subdued">No eligible paid orders are available for this provider.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'numeric']}
                    headings={['Select', 'Order', 'Customer', 'Reference', 'Amount']}
                    rows={eligibleRows}
                  />
                )}
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={eligiblePagination.page > 1}
                    onPrevious={() => loadEligibleOrders(
                      form.provider,
                      eligibleSearch,
                      eligiblePagination.page - 1
                    )}
                    hasNext={eligiblePagination.page < eligiblePagination.totalPages}
                    onNext={() => loadEligibleOrders(
                      form.provider,
                      eligibleSearch,
                      eligiblePagination.page + 1
                    )}
                    label={`Page ${eligiblePagination.page || 1} of ${Math.max(
                      eligiblePagination.totalPages || 1,
                      1
                    )}`}
                  />
                </InlineStack>
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={Boolean(payoutToCollect)}
        onClose={() => !submitting && setPayoutToCollect(null)}
        title="Mark payout as collected?"
        primaryAction={{
          content: 'Mark collected',
          onAction: handleCollect,
          loading: submitting
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setPayoutToCollect(null),
          disabled: submitting
        }]}
      >
        <Modal.Section>
          {payoutToCollect && (
            <Text as="p">
              Confirm that {accountingHelpers.formatCurrency(
                payoutToCollect.net_amount,
                payoutToCollect.currency
              )} was received for payout {payoutToCollect.reference}. This cannot be changed back to pending.
            </Text>
          )}
        </Modal.Section>
      </Modal>
    </>
  );
};

export default PayoutsPage;
